import { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const INITIAL_FORM = {
  email: "",
  authProvider: "",
  reason: "",
  confirmation: false,
};

const AUTH_PROVIDERS = [
  {
    value: "email",
    label: "Correo y contraseña",
    description:
      "Creé mi cuenta usando un correo electrónico y una contraseña.",
    icon: "✉️",
  },
  {
    value: "google",
    label: "Google",
    description: "Inicié sesión utilizando mi cuenta de Google.",
    icon: "G",
  },
  {
    value: "apple",
    label: "Apple",
    description: "Inicié sesión utilizando mi Apple ID.",
    icon: "",
  },
];

function DeleteAccountPage() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    document.documentElement.classList.add("delete-account-html");
    document.body.classList.add("delete-account-body");

    window.scrollTo({
      top: 0,
      behavior: "auto",
    });

    return () => {
      document.documentElement.classList.remove("delete-account-html");
      document.body.classList.remove("delete-account-body");
    };
  }, []);

  function handleChange(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function validateForm() {
    const normalizedEmail = form.email.trim().toLowerCase();

    if (!normalizedEmail) {
      return "Ingresa el correo asociado a tu cuenta de Wiblo.";
    }

    if (!normalizedEmail.includes("@")) {
      return "Ingresa un correo electrónico válido.";
    }

    if (!form.authProvider) {
      return "Selecciona el método que utilizas para iniciar sesión.";
    }

    if (!form.confirmation) {
      return "Debes confirmar que deseas solicitar la eliminación de la cuenta.";
    }

    return "";
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setSuccessMessage("");
    setErrorMessage("");

    const validationError = validateForm();

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    const normalizedEmail = form.email.trim().toLowerCase();
    const normalizedReason = form.reason.trim();

    try {
      setSubmitting(true);

      await addDoc(collection(db, "accountDeletionRequests"), {
        email: normalizedEmail,
        authProvider: form.authProvider,
        reason: normalizedReason || null,

        appName: "Wiblo",
        developerName: "Human Bionics",
        requestType: "full_account_deletion",
        source: "public_web_form",
        status: "pending",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setForm(INITIAL_FORM);

      setSuccessMessage(
        "La solicitud fue enviada correctamente. Human Bionics podrá contactarte por correo para verificar que eres el propietario de la cuenta antes de realizar la eliminación."
      );

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error("Error enviando solicitud de eliminación:", error);

      setErrorMessage(
        "No fue posible enviar la solicitud. Verifica tu conexión e inténtalo nuevamente."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="delete-account-page">
      <div className="delete-account-background-shape delete-account-background-shape--one" />
      <div className="delete-account-background-shape delete-account-background-shape--two" />

      <section className="delete-account-card">
        <header className="delete-account-header">
          <div className="delete-account-logos">
            <div className="delete-account-logo-container">
              <img
                src="/images/LogotipoWiblocolor1.png"
                alt="Logo de Wiblo"
                className="delete-account-logo delete-account-logo--wiblo"
              />
            </div>

            <span className="delete-account-logo-divider" />

            <div className="delete-account-logo-container">
              <img
                src="/images/LogoHumanSlogan.png"
                alt="Logo de Human Bionics"
                className="delete-account-logo delete-account-logo--human"
              />
            </div>
          </div>

          <div className="delete-account-header-text">
            <span className="delete-account-eyebrow">
              Gestión de cuenta
            </span>

            <h1>Eliminar cuenta de Wiblo</h1>

            <p>
              Desde esta página puedes solicitar la eliminación permanente de
              tu cuenta de Wiblo y de los datos personales asociados.
            </p>
          </div>
        </header>

        {successMessage && (
          <div
            className="delete-account-message delete-account-message--success"
            role="status"
            aria-live="polite"
          >
            <div className="delete-account-message-icon">✓</div>

            <div>
              <strong>Solicitud enviada</strong>
              <p>{successMessage}</p>
            </div>
          </div>
        )}

        <div className="delete-account-content">
          <section className="delete-account-information">
            <article className="delete-account-info-section">
              <div className="delete-account-section-number">1</div>

              <div>
                <h2>Envía tu solicitud</h2>

                <p>
                  Ingresa el correo asociado a tu cuenta y selecciona el método
                  que utilizas para iniciar sesión en Wiblo.
                </p>
              </div>
            </article>

            <article className="delete-account-info-section">
              <div className="delete-account-section-number">2</div>

              <div>
                <h2>Verificaremos la cuenta</h2>

                <p>
                  Podremos contactarte mediante el correo indicado para
                  verificar que eres el propietario de la cuenta antes de
                  eliminarla.
                </p>
              </div>
            </article>

            <article className="delete-account-info-section">
              <div className="delete-account-section-number">3</div>

              <div>
                <h2>Eliminaremos tus datos</h2>

                <p>
                  Después de verificar la solicitud, se procederá con la
                  eliminación de la cuenta y de la información asociada.
                </p>
              </div>
            </article>

            <div className="delete-account-data-card">
              <h3>Datos que se eliminarán</h3>

              <ul>
                <li>
                  <span>✓</span>
                  Cuenta de Firebase Authentication.
                </li>

                <li>
                  <span>✓</span>
                  Correo electrónico asociado.
                </li>

                <li>
                  <span>✓</span>
                  Perfil y preferencias del usuario.
                </li>

                <li>
                  <span>✓</span>
                  Progreso, puntuaciones y estadísticas.
                </li>

                <li>
                  <span>✓</span>
                  Datos de clasificación y temporadas.
                </li>

                <li>
                  <span>✓</span>
                  Monedas, fichas y objetos virtuales.
                </li>
              </ul>
            </div>

            <div className="delete-account-retention-card">
              <div className="delete-account-retention-icon">!</div>

              <div>
                <h3>Conservación de información</h3>

                <p>
                  Algunos registros podrán conservarse temporalmente cuando sea
                  necesario para cumplir obligaciones legales, prevenir fraude,
                  resolver disputas o mantener registros de transacciones.
                </p>

                <p>
                  Cuando no exista una obligación de conservación, los datos se
                  eliminarán junto con la cuenta.
                </p>
              </div>
            </div>
          </section>

          <aside className="delete-account-form-wrapper">
            <form
              className="delete-account-form"
              onSubmit={handleSubmit}
              noValidate
            >
              <div className="delete-account-form-header">
                <span>Solicitud de eliminación</span>

                <h2>Identifica tu cuenta</h2>

                <p>
                  Completa los datos utilizados para acceder a Wiblo.
                </p>
              </div>

              <label className="delete-account-field">
                <span className="delete-account-field-label">
                  Correo asociado
                  <strong>*</strong>
                </span>

                <input
                  type="email"
                  placeholder="correo@ejemplo.com"
                  autoComplete="email"
                  value={form.email}
                  onChange={(event) =>
                    handleChange("email", event.target.value)
                  }
                  disabled={submitting}
                  required
                />
              </label>

              <fieldset className="delete-account-provider-fieldset">
                <legend>
                  Método de inicio de sesión
                  <strong>*</strong>
                </legend>

                <div className="delete-account-provider-list">
                  {AUTH_PROVIDERS.map((provider) => {
                    const isSelected =
                      form.authProvider === provider.value;

                    return (
                      <label
                        key={provider.value}
                        className={`delete-account-provider-option ${
                          isSelected
                            ? "delete-account-provider-option--selected"
                            : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="authProvider"
                          value={provider.value}
                          checked={isSelected}
                          onChange={(event) =>
                            handleChange(
                              "authProvider",
                              event.target.value
                            )
                          }
                          disabled={submitting}
                        />

                        <span className="delete-account-provider-icon">
                          {provider.icon}
                        </span>

                        <span className="delete-account-provider-content">
                          <strong>{provider.label}</strong>
                          <small>{provider.description}</small>
                        </span>

                        <span className="delete-account-provider-check">
                          {isSelected ? "✓" : ""}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <label className="delete-account-field">
                <span className="delete-account-field-label">
                  Motivo de la solicitud
                  <small>Opcional</small>
                </span>

                <textarea
                  rows="4"
                  placeholder="Puedes contarnos por qué deseas eliminar tu cuenta."
                  value={form.reason}
                  onChange={(event) =>
                    handleChange("reason", event.target.value)
                  }
                  disabled={submitting}
                  maxLength={500}
                />

                <span className="delete-account-character-count">
                  {form.reason.length}/500
                </span>
              </label>

              <label className="delete-account-confirmation">
                <input
                  type="checkbox"
                  checked={form.confirmation}
                  onChange={(event) =>
                    handleChange(
                      "confirmation",
                      event.target.checked
                    )
                  }
                  disabled={submitting}
                />

                <span>
                  Confirmo que deseo solicitar la eliminación permanente de mi
                  cuenta de Wiblo y de los datos asociados.
                </span>
              </label>

              {errorMessage && (
                <div
                  className="delete-account-message delete-account-message--error"
                  role="alert"
                >
                  <div className="delete-account-message-icon">!</div>

                  <div>
                    <strong>Revisa la información</strong>
                    <p>{errorMessage}</p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="delete-account-submit-button"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="delete-account-spinner" />
                    Enviando solicitud...
                  </>
                ) : (
                  "Solicitar eliminación de cuenta"
                )}
              </button>

              <p className="delete-account-form-notice">
                El envío de esta solicitud no elimina la cuenta inmediatamente.
                El proceso puede tardar hasta 30 días después de la
                verificación.
              </p>
            </form>
          </aside>
        </div>

        <footer className="delete-account-footer">
          <p>
            Wiblo es una aplicación desarrollada por Human Bionics.
          </p>

          <span>© {new Date().getFullYear()} Human Bionics</span>
        </footer>
      </section>
    </main>
  );
}

export default DeleteAccountPage;